import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DropZone from 'components/dropzone/DropZone';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import RadioGroup from 'components/final-form/RadioGroup';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import { enabledTestFrequencies } from 'components/guarantors/employee/employeeUtils';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { Component, Fragment } from "react";
import { FileRejection } from 'react-dropzone';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Icon, Loader, Popup, RadioProps } from 'semantic-ui-react';
import { getAllCompanies, getCompanyExternalCodeFromCompanyArticleGroups } from 'services/companyServices';
import { importEmployees, uploadEmployeesFile } from 'services/employeeImportServices';
import { getCompanyShippableMedicaments } from 'services/medicamentServices';
import styled from 'styled-components';
import { TestFrequency, TestRegistrationType } from 'ts-types/api.enums';
import {
  CompanyDto,
  EmployeeImportDto,
  EmployeeImportRequest,
  UpsertEmployeeDto,
  UpsertEmployeeImportDto
} from 'ts-types/api.types';
import Input from 'components/final-form/Input';
import createDecorator from 'final-form-calculate';

const StyledGridColumn = styled(Grid.Column)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

const SearchFormDiv = styled.div`

  .actions-row {

    display: flex;
    flex-direction: row;
    align-items: baseline;

    button {
      margin-right: 1rem;
    }

    .submit-message {
      display: flex;
      flex-direction: row;
      align-self: center;

      .ui.inline.loader.active,
      .ui.inline.loader.visible {
        margin: 0 0.9rem;
      }
    }
  }
`;

const ResultsPane = styled.div`
  flex: 0 0 auto;
  margin-right: 15px;
  min-width: 15rem;
  max-width: 36rem;

  .results-header {
    font-weight: bold;
    font-size: 1.1rem;
    border-bottom: 1px solid #e5e5e5;
    margin-bottom: 0.5rem;
  }

  .results-container {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr);
    row-gap: 0.35rem;
    column-gap: 1.2rem;

    .import-error {
      color: #575757;
    }
  }
`;

interface Props extends GuarantorsConsumerRenderProps,
    AuthConsumerRenderProps,
    NotificationSystemProps,
    MessageSourceProps {
}

interface State {
  referenceDataLoaded: boolean,
  companyId: number,
  companies: Array<CompanyDto>,
  companyOptions: Array<DropdownOption>,
  medicaments: Array<DropdownOption>,
  testTypes: Array<DropdownOption>,
  initialValuesLoad: boolean,
  foundEmployees: Array<EmployeeImportDto>,
  employeesLoaded: boolean,
  employee?: UpsertEmployeeDto,
  employeeId?: number,

  testRegistrationTypes: Array<RadioProps>,
  testFrequencies: Array<RadioProps>,

  submitting: boolean,
  errorMessage?: String,

  cancelTokenSource: CancelTokenSource,
}

class EmployeesImportView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialEmployeeImportValues: Partial<UpsertEmployeeImportDto> = {};

  constructor(props: Props) {
    super(props);

    const {currentUser, getMessage} = props;

    const cancelTokenSource = axios.CancelToken.source();

    const testRegTypesLabels: Array<string> = Object.values(TestRegistrationType);
    const testFrequenciesLabels: Array<string> = enabledTestFrequencies;

    const testFrequencies = generateRadioButtonOptionsFromEnum(
        testFrequenciesLabels,
        "testFrequency",
        getMessage
    );

    const testRegistrationTypes = generateRadioButtonOptionsFromEnum(
        testRegTypesLabels,
        "testRegistrationType",
        getMessage
    );

    const selectedCompanyId = props.selectedCompanyId || -1;

    this.initialEmployeeImportValues = {
      mandateId: currentUser!.userConfig.mandateId,
      companyId: selectedCompanyId,
      testFrequency: TestFrequency.WEEK_ONE,
      testRegistrationType: TestRegistrationType.CENTRAL,
      acceptContractualDocuments: false
    };

    this.state = {
      referenceDataLoaded: false,
      companyId: selectedCompanyId,
      companies: [],
      companyOptions: [],
      medicaments: [],
      testTypes: [],
      initialValuesLoad: true,
      foundEmployees: [],
      employeesLoaded: true,
      testRegistrationTypes: testRegistrationTypes,
      testFrequencies: testFrequencies,

      submitting: false,
      cancelTokenSource
    };

    this.loadReferenceData(cancelTokenSource);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    const {getMessage, currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    try {
      const companies = await getAllCompanies(mandateId, cancelTokenSource);

      if (companies.length > 0) {

        this.setState({
          companies: companies,
          companyOptions: mapToDropdownOptionArrayWithSep(companies, ["description", "city"], "id")
        });

        const company = companies.find(c => c.id === this.props.selectedCompanyId);
        if (company) {
          this.initialEmployeeImportValues.acceptContractualDocuments = company.acceptCD;
          //@ts-ignore
          this.initialEmployeeImportValues.acceptContractualDocumentsRemark = company.acceptCDRemark
        }

        let selectedCompanyId = this.state.companyId;

        if (!this.props.selectedCompanyId) {
          selectedCompanyId = companies[0].id;
          this.props.setSelectedCompanyId(selectedCompanyId);
          this.initialEmployeeImportValues.companyId = selectedCompanyId;
          this.setState({
            companyId: selectedCompanyId,
          });
        }

        const medicaments = await getCompanyShippableMedicaments(selectedCompanyId, cancelTokenSource);
        let medicamentOptions = mapToDropdownOptionArray(medicaments, "description", "id", "id");
        if (medicamentOptions.length > 0) {
          medicamentOptions.unshift({value: null, text: "", key: -1});
        }
        this.setState({
          medicaments: medicamentOptions
        });
        if (medicaments.length > 0) {
          this.initialEmployeeImportValues.medicamentId = medicaments[0].id;
        }
        const companyTestTypes = await getCompanyExternalCodeFromCompanyArticleGroups(
            selectedCompanyId, cancelTokenSource);

        this.setState({
          testTypes: stringArrayToDropdownOptionArray(
              companyTestTypes, getMessage, "enum.InterventionExternalCode")
        });
        if (companyTestTypes.length > 0) {
          this.initialEmployeeImportValues.testType = companyTestTypes[0];
        }
      }
    } finally {
      this.setState({
        referenceDataLoaded: true,
        initialValuesLoad: false
      });
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      "INVALID_DOCUMENT_INPUT"
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  onDropAccepted = (acceptedFiles: File[], form: FormApi) => {
    const {companies, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        employeesLoaded: true
      })
    };

    form.change("file", acceptedFiles[0]);
    const values: Partial<UpsertEmployeeImportDto> = form.getState().values;

    if (companies && companies.length > 0) {

      this.setState({employeesLoaded: false});
      uploadEmployeesFile(values, cancelTokenSource)
          .then(response => {
                this.setState({
                  foundEmployees: response
                })
              }
          )
          .catch((e) => this.handleError(e.response.data))
          .finally(onFinally);
    }
  };

  onDropRejected = (rejections: FileRejection[]) => {
    // TODO: Handle drop rejected
    console.log(rejections, "FILE REJECTIONS");
  };

  cancelImport = (form: FormApi) => {
    form.change("file", undefined);
    form.change("medicamentId", undefined);
    this.setState({
      foundEmployees: []
    })
  }

  handleChange = ({values}: FormState, form: FormApi) => {

    const {getMessage} = this.props;
    const {companyId, cancelTokenSource, companies} = this.state;

    if (values.companyId && companyId !== values.companyId) {

      this.cancelImport(form);

      this.setState({
        companyId: values.companyId
      });
      this.props.setSelectedCompanyId(values.companyId);

      const company = companies.find(c => c.id === values.companyId);
      if (company) {
        form.change("acceptContractualDocuments", company.acceptCD);
        form.change("acceptContractualDocumentsRemark", company.acceptCDRemark);

      }

      getCompanyShippableMedicaments(values.companyId, cancelTokenSource)
          .then(response => {
            let medicaments = mapToDropdownOptionArray(response, "description", "id", "id");
            if (medicaments.length > 0) {
              medicaments.unshift({value: null, text: "", key: -1});
            }
            this.setState({
              medicaments: medicaments
            })
          });

      getCompanyExternalCodeFromCompanyArticleGroups(values.companyId, cancelTokenSource)
          .then(response => {
            this.setState({
              testTypes: stringArrayToDropdownOptionArray(
                  response, getMessage, "enum.InterventionExternalCode")
            })
          })
    }
  };

  handleSubmit = async (values: Partial<EmployeeImportRequest>, form: FormApi) => {

    if (this.state.submitting) {
      return;
    }

    const {
      currentUser,
      selectionNotificationSystem,
      getMessage,
      getMessageWithNamedParams
    } = this.props;

    const {
      foundEmployees,
      cancelTokenSource,
      companies
    } = this.state;

    let employees: Array<EmployeeImportDto> = foundEmployees.filter(employee => employee.selected);

    const request: EmployeeImportRequest = {
      mandateId: currentUser!.userConfig.mandateId,
      employees: employees,
      companyId: values.companyId!,
      medicamentId: values.medicamentId!,
      testType: values.testType!,
      testFrequency: values.testFrequency!,
      testRegistrationType: values.testRegistrationType!,
      acceptContractualDocuments: values.acceptContractualDocuments!,
      acceptContractualDocumentsRemark: values.acceptContractualDocumentsRemark
          ? values.acceptContractualDocumentsRemark
          : ""
    }

    this.setState({submitting: true});

    let response: string;
    try {
      response = await importEmployees(request, cancelTokenSource);
    } catch (ex) {
      this.handleError(ex.response.data);
      return;
    } finally {
      this.setState({
        submitting: false
      });
    }

    //@ts-ignore
    const notification = selectionNotificationSystem.current;
    if (notification) {
      const company = companies.find(c => c.id === values.companyId);
      let companyName: string = company ? "" + company.description : "company";

      const msgSuccess = getMessageWithNamedParams("employee.msgSuccess", {
        employeesCount: response,
        company: companyName
      });

      notification.addNotification({
        title: getMessage("employee.importSuccess"),
        message: msgSuccess,
        level: 'success'
      });
    }

    this.cancelImport(form);
  }

  resetAcceptCDRemark = createDecorator({
    field: 'acceptContractualDocuments',
    updates: {
      acceptContractualDocumentsRemark: (fieldValue, allValues?: Partial<EmployeeImportRequest>) => {
        return fieldValue && allValues ? allValues.acceptContractualDocumentsRemark : "";
      }
    }
  });

  render(): React.ReactNode {
    const {employeesLoaded, foundEmployees} = this.state;
    const {getMessage} = this.props;

    const countAllEmployees = foundEmployees.length;
    const countSelectedEmployees = foundEmployees.filter(employee => employee.selected).length;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <SearchFormDiv>
            {this.renderEmployeesImportSegment()}
          </SearchFormDiv>
          <div>
            {getMessage("employee.selectedParticipants")}:&nbsp;&nbsp;
            <strong>{countSelectedEmployees}/{countAllEmployees}</strong>
          </div>
          <StyledViewTableRow id="employeeImportTable">
            {
              !employeesLoaded
                  ? <LoaderComponent message={getMessage("employee.loading")}/>
                  : foundEmployees && foundEmployees.length > 0 ? this.renderEmployeesSegment() : null
            }
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  }

  renderEmployeesImportSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => this.handleSubmit}
            initialValues={this.initialEmployeeImportValues}
            decorators={[
              this.resetAcceptCDRemark
            ]}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderTestTypesSection = (values: any): React.ReactNode => {

    const {medicaments, testRegistrationTypes, testFrequencies, testTypes} = this.state;
    const {getMessage} = this.props;

    const disableAcceptCDRemark = !values.acceptContractualDocuments;

    return (
        <React.Fragment>
          <Grid.Row>
            <Grid.Column style={{paddingTop: "0.5rem"}}>
              <Header as="h5">{getMessage("employee.testInfo")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("employee.testType")}:`}
            </Grid.Column>
{/*
            <Grid.Column width={5}>
              <Field
                  name="medicamentId"
                  component={Select}
                  options={medicaments}
                  fluid
                  clearable
              />
            </Grid.Column>
*/}
            <Grid.Column width={9}>
              <Field
                  name="testType"
                  component={Select}
                  options={testTypes}
                  fluid
                  clearable
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("employee.testRegistrationType")}:`}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="testRegistrationType"
                  component={RadioGroup}
                  radioDefinitions={testRegistrationTypes}
                  radiowidth="50%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {`${getMessage("employee.testFrequency")}:`}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="testFrequency"
                  component={RadioGroup}
                  radioDefinitions={testFrequencies}
                  radiowidth="50%"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={6}>
              <Field
                  name="acceptContractualDocuments"
                  component={CheckBox}
                  label={`${getMessage("employee.acceptContractDocuments")}`}
              />
            </Grid.Column>
            <Grid.Column width={6} verticalAlign="middle">
              <Field
                  name="acceptContractualDocumentsRemark"
                  component={Input}
                  disabled={disableAcceptCDRemark}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
        </React.Fragment>
    );
  };


  renderSearchForm = ({values, form}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {companyOptions, companyId, foundEmployees, errorMessage, initialValuesLoad, companies} = this.state;

    const uploadMessage = values.file ? values.file.name : getMessage("employee.upload");

    const countSelectedEmployees = foundEmployees.filter(e => e.selected).length;
    const disableImport = !(countSelectedEmployees > 0 && values.testType !== undefined && values.testType !== null);

    const canUploadFile = companyId > 0;
    const company = companies.find(c => c.id === values.companyId);
    if (initialValuesLoad && company) {
      form.change("acceptContractualDocuments", company.acceptCD);
      form.change("acceptContractualDocumentsRemark", company.acceptCDRemark);
    }

    return (
        <PxGrid columns={2}>
          {errorMessage
          && (
              <Grid.Row>
                <Grid.Column width={16}>
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
                </Grid.Column>
              </Grid.Row>
          )
          }
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("employee.import")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <StyledGridColumn width={5}>
              <PxGrid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header size="tiny">{getMessage("company.title")}</Header>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Field
                        name="companyId"
                        component={Select}
                        fluid
                        options={companyOptions}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <Header size="tiny">{getMessage("employee.upload")}</Header>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <DropZone
                        multiple={false}
                        disabled={!canUploadFile}
                        message={uploadMessage}
                        onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, form)}
                        onDropRejected={this.onDropRejected}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div className="actions-row">
                      <Button
                          type="button"
                          primary
                          compact
                          disabled={disableImport || this.state.submitting}
                          onClick={() => this.handleSubmit(values, form)}
                      >
                        {getMessage("misantoImport.import")}
                      </Button>

                      <Button
                          compact
                          type="button"
                          onClick={() => this.cancelImport(form)}
                      >{getMessage("button.cancel")}
                      </Button>

                      {
                        this.state.submitting &&
                        <div className="submit-message">
                          <Loader inline active size="small"/>
                          <div>{getMessage("employee.importing")}</div>
                        </div>
                      }
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </StyledGridColumn>
            <Grid.Column width={11}>
              <PxGrid>
                {this.renderTestTypesSection(values)}
              </PxGrid>
            </Grid.Column>
          </Grid.Row>
          <FormSpy subscription={{pristine: true, values: true}}
                   onChange={formState => this.handleChange(formState, form)}/>
        </PxGrid>
    )
  };

  employeeCellRenderer = ({rowData}: any) => {
    if (rowData.firstName || rowData.name) {
      return `${rowData.firstName} ${rowData.name}`;
    }

    if (rowData.email) {
      return rowData.email;
    }

    return "";
  };

  birthDateCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <div>{moment(cellData).format("DD.MM.YYYY")}</div>;
    }
    return "";
  }


  genderCellRenderer = ({cellData}: any) => {

    if (cellData === 0) {
      return "M";
    }

    if (cellData === 1) {
      return "F";
    }

    return "n/a";
  };

  zipCityCellRenderer = ({rowData}: any) => {
    return `${rowData.zip} ${rowData.city}`;
  };

  divisionCellRenderer = ({cellData}: any) => {
    if (cellData && cellData.length > 0) {
      return cellData;
    }
    return "";
  };

  testTypeCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData && cellData.length > 0) {
      return getMessage(`testType.${cellData}`)
    }
    return "";
  }

  testFrequencyCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData && cellData.length > 0) {
      return getMessage(`testFrequency.${cellData}`)
    }
    return "";
  };

  bagCellRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;
    const insuranceValue = rowData.insuranceName;
    if (insuranceValue) {
      return <>
        {
          rowData.unknownGuarantor &&
          <Icon name="warning circle" color="orange" title={getMessage("error.UNKNOWN_BAG_NUMBER")} />
        }
        <span>{insuranceValue}</span>
      </>
    }
    return "";
  };

  defaultStringValueCellRenderer = (value: string) => ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const errors = rowData.importErrorDtoList;
    const hoverMessage = getMessage("error.VALUE_TOO_LONG");
    let error = {};
    if (errors) {
      error = errors.find((error: { field: string; }) => error.field === value);
    }

    if (cellData) {
      if (error) {
        return <>
          {
            <Popup
                trigger={<Icon name="times circle" color="red"/>}
                content={hoverMessage}
                size="large"
                position='top center'
            />
          }
          <span>{cellData}</span>
        </>
      }
      return cellData;
    }
    return "";

  };

  employeesRowGetter = ({index}: any) => {
    const {foundEmployees} = this.state;

    Object.assign(foundEmployees[index], {index: index + 1});

    return foundEmployees[index];
  };

  employeesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundEmployees} = this.state;

    const employee = foundEmployees[index];

    let rowStyle = {...style};
    if (employee.invalid) {
      rowStyle = {...rowStyle, color: "rgb(198, 198, 203)"};
    } else if (employee.exists) {
      rowStyle = {...rowStyle, color: "rgb(48 145 218)"};
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={rowStyle}
            onDoubleClick={() => this.selectEmployee(index)}
        >
          {columns}
        </div>
    );
  };

  selectHeaderColumn = () => {

    const {foundEmployees} = this.state;
    const countSelectableEmployees = foundEmployees.filter(e => !e.invalid).length;
    const countSelectedEmployees = foundEmployees.filter(e => e.selected).length;
    const indeterminate = countSelectedEmployees > 0 && countSelectedEmployees < countSelectableEmployees;
    const noSelectedEmployees = countSelectedEmployees === 0 && countSelectableEmployees > 0;
    const allEmployeesSelected = countSelectableEmployees > 0 && countSelectedEmployees === countSelectableEmployees;

    return (
        <CheckboxSc
            checked={countSelectedEmployees > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedEmployees || indeterminate) {
                  let employees = [...foundEmployees];
                  const selectedEmployees = employees.map(e => {
                    if (!e.invalid) {
                      return {...e, selected: true};
                    }
                    return e;
                  });
                  this.setState({
                    foundEmployees: selectedEmployees
                  });
                }

                if (allEmployeesSelected) {
                  let employees = [...foundEmployees];
                  const selectedEmployees = employees.map(e => ({...e, selected: false}));
                  this.setState({
                    foundEmployees: selectedEmployees
                  });
                }

              }
            }
            disabled={countSelectableEmployees === 0}
        />
    )
  };


  selectEmployee = (index: number) => {
    const {foundEmployees} = this.state;
    let employee = foundEmployees[index];
    if (employee && !employee.invalid) {
      let employees = [...foundEmployees];
      employees[index].selected = !employees[index].selected;
      this.setState({
        foundEmployees: employees
      })
    }
  }

  employeeSelectCellRenderer = (data: any) => {

    const {cellData, rowData} = data;
    const {getMessage} = this.props;
    const {foundEmployees} = this.state;

    const index = cellData - 1;
    const employee = foundEmployees[index];

    let showErrors = employee.invalid;
    const importErrors = employee.importErrors;

    if (showErrors && importErrors) {
      const trigger = (
          <Icon className="anamnesis-details-icon" name="clipboard outline" fitted color="grey"/>
      );

      const content = (
          <ResultsPane>
            <div className="results-header">{getMessage("employee.importError")}</div>
            <div className="results-container">
              {
                importErrors.map(error => {
                  return (
                      <Fragment key={`error-row-${error}`}>
                        <div className="import-error">
                          {getMessage(`importErrorType.${error}`)}
                        </div>
                      </Fragment>
                  );
                })
              }
            </div>
          </ResultsPane>
      );

      return (
          <div>
            <Popup
                trigger={trigger}
                popperModifiers={{
                  preventOverflow: {
                    boundary: document.getElementById("employeeImportTable")
                  }
                }}
                content={content}
                flowing
                on="hover"
                size="large"
                position="right center"
            />
          </div>
      );
    }

    return (
        <CheckboxSc
            checked={rowData.selected}
            disabled={rowData.invalid}
            onChange={() => this.selectEmployee(index)}
        />
    )
  };

  renderEmployeesSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundEmployees} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundEmployees.length}
            rowGetter={this.employeesRowGetter}
            rowRenderer={this.employeesRowRenderer}
            columns={[
              {
                width: 50,
                label: this.selectHeaderColumn(),
                dataKey: "index",
                cellRenderer: this.employeeSelectCellRenderer
              },
              {
                width: 250,
                label: (getMessage("employee.name")),
                dataKey: "name"
              },
              {
                width: 250,
                label: (getMessage("employee.firstName")),
                dataKey: "firstName"
              },
              {
                width: 150,
                label: (getMessage("patient.birthDate")),
                dataKey: "birthDate",
                cellRenderer: this.birthDateCellRenderer
              },
              {
                width: 400,
                label: (getMessage("employee.email")),
                dataKey: "companyEmail"
              },
              {
                width: 100,
                label: (getMessage("employee.language")),
                dataKey: "language"
              },
              {
                width: 300,
                label: (getMessage("employee.division")),
                dataKey: "divisionName",
                cellRenderer: this.divisionCellRenderer
              },
              {
                width: 300,
                label: (getMessage("employee.remark")),
                dataKey: "remark"
              },
              {
                width: 300,
                label: (getMessage("employee.street")),
                dataKey: "street",
                cellRenderer: this.defaultStringValueCellRenderer("street")
              },
              {
                width: 100,
                label: (getMessage("employee.zip")),
                dataKey: "zip",
                cellRenderer: this.defaultStringValueCellRenderer("zip")
              },
              {
                width: 300,
                label: (getMessage("employee.city")),
                dataKey: "city"
              },
              {
                width: 300,
                label: (getMessage("employee.passportNumber")),
                dataKey: "passportNumber",
                cellRenderer: this.defaultStringValueCellRenderer("passportNumber")
              },
              {
                width: 300,
                label: (getMessage("employee.ahvNumber")),
                dataKey: "ahvNumber"
              },
              {
                width: 300,
                label: (getMessage("bagNumber")),
                dataKey: "insuranceName",
                cellRenderer: this.bagCellRenderer
              },
              {
                width: 300,
                label: (getMessage("employee.telephone")),
                dataKey: "mobilePhone",
                cellRenderer: this.defaultStringValueCellRenderer("mobilePhone")
              },
              {
                width: 300,
                label: (getMessage("employee.sex")),
                dataKey: "gender"
              }
            ]}
        />

    );
  };

}

export default withAuthContext(
    withMessages(
        withNotificationSystemContext(
            withGuarantorsContext(
                EmployeesImportView))));
